/**
 * Üst şerit — "neredeyim" + genel eylemler.
 *
 * Sol: mobil menü düğmesi, grup adı, ekran başlığı ve tek satırlık açıklaması
 *      (`config/navigation.jsx`'ten).
 * Sağ: dil seçici ve tema anahtarı.
 */
import { MenuOutlined } from "@ant-design/icons";
import { Button, Dropdown, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { findNavItem } from "../../config/navigation";
import { useTheme } from "../../theme/ThemeProvider";

/* antd ikon setinde ay/güneş yok — menüdeki diğer ikonlarla aynı 1em
   ölçüsünde, `currentColor` ile boyanan satır içi SVG'ler. */
const MoonIcon = () => (
  <span role="img" className="anticon">
    <svg viewBox="0 0 1024 1024" width="1em" height="1em" fill="currentColor">
      <path d="M885 592a352 352 0 01-453-453 368 368 0 10453 453z" />
    </svg>
  </span>
);

const SunIcon = () => (
  <span role="img" className="anticon">
    <svg
      viewBox="0 0 1024 1024"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeWidth="72"
      strokeLinecap="round"
    >
      <circle cx="512" cy="512" r="196" />
      <path d="M512 96v96m0 640v96M96 512h96m640 0h96M218 218l68 68m452 452l68 68M806 218l-68 68M286 738l-68 68" />
    </svg>
  </span>
);

const Topbar = ({ onOpenMobileNav }) => {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const { isDark, toggle } = useTheme();

  const navItem = findNavItem(pathname);
  const title = navItem ? t(navItem.label) : t("nav.items.home");
  const desc = navItem?.desc ? t(navItem.desc) : "";
  const group = navItem?.section ? t(navItem.section.label) : "";

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    try {
      localStorage.setItem("lng", lng);
    } catch {
      /* localStorage kapalıysa tercih yalnız bu oturumda geçerli olur */
    }
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-3 border-b border-line bg-surface/95 px-4 backdrop-blur md:px-6">
      {/* Mobil menü */}
      <Button
        type="text"
        icon={<MenuOutlined />}
        onClick={onOpenMobileNav}
        aria-label={t("shell.menu")}
        className="laptop:hidden"
      />

      {/* Konum + başlık */}
      <div className="min-w-0 flex-1">
        {group && (
          <div className="hidden text-2xs font-semibold uppercase tracking-label text-content-faint tablet:block">
            {group}
          </div>
        )}
        <div className="flex min-w-0 items-baseline gap-2">
          <h1 className="truncate text-[15px] font-semibold leading-tight text-content">
            {title}
          </h1>
          {desc && (
            <span className="hidden truncate text-xs text-content-muted desktop:block">
              — {desc}
            </span>
          )}
        </div>
      </div>

      {/* Eylemler */}
      <div className="flex shrink-0 items-center gap-2">
        <Dropdown
          trigger={["click"]}
          menu={{
            selectable: true,
            selectedKeys: [i18n.language === "en" ? "en" : "tr"],
            items: [
              { key: "tr", label: "Türkçe" },
              { key: "en", label: "English" },
            ],
            onClick: ({ key }) => changeLanguage(key),
          }}
        >
          <Button type="text" aria-label={t("shell.language")}>
            {i18n.language === "en" ? "EN" : "TR"}
          </Button>
        </Dropdown>

        <Tooltip title={isDark ? t("shell.theme.dark") : t("shell.theme.light")}>
          <Button
            type="text"
            icon={isDark ? <MoonIcon /> : <SunIcon />}
            onClick={toggle}
            aria-label={t("shell.theme.toggle")}
            aria-pressed={isDark}
          />
        </Tooltip>
      </div>
    </header>
  );
};

export default Topbar;
